import * as Popover from '@radix-ui/react-popover';
import { useTranslation } from 'react-i18next';
import { LOCALES, type Locale } from '@jisy/shared';
import { currentLocale, setLocale } from '../i18n/index.js';
import { useLocaleMutation } from '../api/queries.js';
import { LocaleFlag } from './LanguageFlags.js';
import styles from './LanguagePicker.module.css';

/**
 * Выбор языка интерфейса. Макета для него нет — собран по токенам как обычный поповер списка.
 *
 * До входа выбор сохраняется только в браузере, после входа уходит на сервер: язык привязан
 * к пользователю, а не к сайту Jira, поэтому у коллег он может быть свой.
 */
export function LanguagePicker({
  compact = false,
  authenticated = false,
}: {
  compact?: boolean;
  authenticated?: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const mutation = useLocaleMutation();
  const active = currentLocale();

  function choose(locale: Locale): void {
    if (locale === active) return;
    if (authenticated) {
      mutation.mutate(locale);
      return;
    }
    setLocale(locale);
  }

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={compact ? styles.triggerCompact : styles.trigger}
          aria-label={t('settings.language')}
        >
          <LocaleFlag locale={active} className={styles.flag} />
          <span className={styles.triggerLabel}>{active.toUpperCase()}</span>
          <span className={styles.chevron} aria-hidden="true">
            ▾
          </span>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content className={styles.content} align="end" sideOffset={6}>
          <div className={styles.heading}>{t('settings.language')}</div>
          <div className={styles.list}>
            {LOCALES.map((locale) => (
              <button
                type="button"
                key={locale}
                className={`${styles.option} ${locale === active ? styles.optionActive : ''}`}
                onClick={() => choose(locale)}
              >
                <span className={styles.optionLabel}>
                  <LocaleFlag locale={locale} className={styles.flag} />
                  {t(`locales.${locale}`)}
                </span>
                {locale === active && (
                  <span className={styles.check} aria-hidden="true">
                    ✓
                  </span>
                )}
              </button>
            ))}
          </div>
          <p className={styles.hint}>{t('settings.languageHint')}</p>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
